import React, { useEffect, useMemo, useRef, useState } from 'react';
import GridLayout, { WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { LayoutGrid, Plus, RotateCcw, Search, Settings2, SquarePlus, Trash2 } from 'lucide-react';

import { useDashboardStore } from '../../store/dashboardStore';
import { useAuthStore } from '../../store/authStore';
import { selectSource, useWorkspaceDataStore } from '../../store/workspaceDataStore';
import { WIDGET_GROUPS, WidgetRegistry } from './WidgetRegistry';
import { WidgetWrapper } from './WidgetWrapper';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/Dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/DropdownMenu';
import { cn } from '@/lib/utils';

// Desktop-first, full-width dashboard: a single fixed 12-column grid whose
// width is measured from its container. We intentionally do NOT use the
// Responsive wrapper here — the store keeps one canonical layout array, and
// letting RGL recompact it per breakpoint would save a narrow (stacked) layout
// back over the desktop one, permanently collapsing every widget into one
// column.
const FixedGridLayout = WidthProvider(GridLayout);

/** Below this width the 12-column grid is unreadable, so cards stack in one column. */
const STACK_BELOW_PX = 768;
const ROW_PX = 60;
const GAP_PX = 16;

/** Width of an element, kept current with a ResizeObserver. */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
};

/** Greeting + today's date + a one-line pulse from the People dashboard data. */
function PageHeading() {
  const user = useAuthStore((s) => s.user);
  const pd = useWorkspaceDataStore(selectSource('peopleDashboard'));
  const load = useWorkspaceDataStore((s) => s.load);
  useEffect(() => { load('peopleDashboard'); }, [load]);

  const d: any = pd.data;
  const firstName = (user?.name || '').split(/\s+/)[0];
  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
  const pulse: string[] = [];
  if (d?.org) {
    pulse.push(`${d.org.attendanceToday.present} of ${d.org.headcount.active} people in today`);
    if (d.org.pendingLeave?.total) pulse.push(`${d.org.pendingLeave.total} leave ${d.org.pendingLeave.total === 1 ? 'request' : 'requests'} to review`);
    if (d.org.pendingRegularizations?.total) pulse.push(`${d.org.pendingRegularizations.total} attendance ${d.org.pendingRegularizations.total === 1 ? 'adjustment' : 'adjustments'} to review`);
  } else if (d?.me) {
    pulse.push(d.me.attendance.status === 'checked_in' ? 'You are checked in' : d.me.attendance.status === 'on_leave' ? 'You are on leave today' : "You haven't checked in yet");
  }

  return (
    <div className="min-w-0">
      <h1 className="font-display text-page-title text-foreground">
        {greeting()}{firstName ? `, ${firstName}` : ''}
      </h1>
      <p className="mt-0.5 truncate text-sm text-muted-foreground">
        {[today, ...pulse].join(' · ')}
      </p>
    </div>
  );
}

export function DashboardEngine({ topSlot }: { topSlot?: React.ReactNode } = {}) {
  const { tabs, activeTabId, updateLayout, addWidget, addTab, setActiveTab, removeTab, resetTab } = useDashboardStore();
  const activeTab = tabs.find((t) => t.id === activeTabId) || tabs[0];

  const [canvasRef, canvasWidth] = useWidth<HTMLDivElement>();
  const stacked = canvasWidth > 0 && canvasWidth < STACK_BELOW_PX;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [newTabOpen, setNewTabOpen] = useState(false);
  const [newTabName, setNewTabName] = useState('');

  const onTab = useMemo(() => new Set(activeTab?.widgets.map((w) => w.type) ?? []), [activeTab]);
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const entries = Object.entries(WidgetRegistry).filter(([, c]) =>
      !q || c.title.toLowerCase().includes(q) || c.description.toLowerCase().includes(q) || c.group.toLowerCase().includes(q));
    return WIDGET_GROUPS.map((g) => ({ group: g, items: entries.filter(([, c]) => c.group === g) })).filter((g) => g.items.length);
  }, [query]);

  const createTab = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTabName.trim()) return;
    addTab(newTabName.trim());
    setNewTabName('');
    setNewTabOpen(false);
  };

  const pickWidget = (type: string) => {
    if (!activeTab) return;
    const config = WidgetRegistry[type];
    addWidget(activeTab.id, type, config?.defaultW, config?.defaultH);
    setPickerOpen(false);
    setQuery('');
  };

  if (!activeTab) return <div className="p-8">No active dashboard.</div>;
  const builtIn = activeTab.id === 'default-main' || activeTab.id === 'default-hr';

  return (
    <div className="relative flex h-full flex-1 flex-col overflow-hidden bg-background">
      <div className="flex-1 overflow-y-auto">
        {topSlot && (
          <div className="mx-auto w-full max-w-screen-2xl px-4 pt-4 md:px-6">{topSlot}</div>
        )}
        <div className="mx-auto w-full max-w-screen-2xl px-4 pb-8 pt-6 md:px-6">
          {/* Page header: who / when / today's pulse, then the view controls */}
          <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
            <PageHeading />
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary"><Settings2 /> Customize</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => setNewTabOpen(true)}>
                    <SquarePlus className="size-4" /> New dashboard tab
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => resetTab(activeTab.id)}>
                    <RotateCcw className="size-4" /> {builtIn ? 'Reset this tab to default' : 'Clear this tab'}
                  </DropdownMenuItem>
                  {tabs.length > 1 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem destructive onClick={() => removeTab(activeTab.id)}>
                        <Trash2 className="size-4" /> Delete “{activeTab.title}”
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button onClick={() => setPickerOpen(true)}><Plus /> Add widget</Button>
            </div>
          </header>

          {/* Dashboard tabs (segmented) */}
          <div className="mt-5 flex items-center gap-2">
            <div role="tablist" aria-label="Dashboards" className="inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-lg bg-secondary p-1">
              {tabs.map((tab) => {
                const active = tab.id === activeTab.id;
                return (
                  <Button
                    key={tab.id}
                    variant="unstyled"
                    size="unstyled"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'h-8 whitespace-nowrap rounded-md px-3 text-sm font-medium transition-colors',
                      active ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {tab.title}
                  </Button>
                );
              })}
            </div>
            <Button variant="ghost" size="icon-sm" aria-label="New dashboard tab" onClick={() => setNewTabOpen(true)}>
              <Plus />
            </Button>
          </div>

          {/* Grid canvas */}
          <div className="mt-4" ref={canvasRef}>
            {activeTab.widgets.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border-strong bg-card px-6 py-16 text-center">
                <LayoutGrid className="size-8 text-muted-foreground" />
                <h2 className="mt-3 font-display text-section-title text-foreground">This dashboard is empty</h2>
                <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                  Add widgets for the numbers you check every day — attendance, tickets, assets, tasks.
                </p>
                <div className="mt-4 flex gap-2">
                  {builtIn && <Button variant="secondary" onClick={() => resetTab(activeTab.id)}><RotateCcw /> Use default widgets</Button>}
                  <Button variant="secondary" onClick={() => setPickerOpen(true)}><Plus /> Add widget</Button>
                </div>
              </div>
            ) : stacked ? (
              // Phone / narrow window: one column in reading order (row, then left to right);
              // the saved desktop layout is left untouched and no dragging happens here.
              <div className="flex flex-col gap-4">
                {[...activeTab.layout].sort((a, b) => a.y - b.y || a.x - b.x).map((l) => {
                  const widget = activeTab.widgets.find((w) => w.id === l.i);
                  const registryItem = widget && WidgetRegistry[widget.type];
                  if (!widget || !registryItem) return null;
                  const Component = registryItem.component;
                  return (
                    <div key={widget.id} style={{ minHeight: Math.max(l.h * ROW_PX + (l.h - 1) * GAP_PX, 180) }}>
                      <WidgetWrapper id={widget.id} tabId={activeTab.id} title={registryItem.title}>
                        <Component />
                      </WidgetWrapper>
                    </div>
                  );
                })}
              </div>
            ) : (
              <FixedGridLayout
                className="layout"
                layout={activeTab.layout}
                cols={12}
                rowHeight={ROW_PX}
                onLayoutChange={(layout: any) => updateLayout(activeTab.id, layout)}
                draggableHandle=".widget-drag-handle"
                isBounded={false}
                margin={[GAP_PX, GAP_PX]}
                containerPadding={[0, 0]}
              >
                {activeTab.widgets.map((widget) => {
                  const registryItem = WidgetRegistry[widget.type];
                  if (!registryItem) return <div key={widget.id} />;
                  const Component = registryItem.component;
                  return (
                    <div key={widget.id}>
                      <WidgetWrapper id={widget.id} tabId={activeTab.id} title={registryItem.title}>
                        <Component />
                      </WidgetWrapper>
                    </div>
                  );
                })}
              </FixedGridLayout>
            )}
          </div>
        </div>
      </div>

      {/* Widget picker */}
      <Dialog open={pickerOpen} onOpenChange={(o) => { setPickerOpen(o); if (!o) setQuery(''); }}>
        <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col">
          <DialogHeader>
            <DialogTitle>Add a widget</DialogTitle>
            <DialogDescription>Widgets show live data from each hub. You can drag and resize them afterwards.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search widgets" className="pl-9" aria-label="Search widgets" />
          </div>
          <div className="-mx-1 min-h-0 flex-1 space-y-5 overflow-y-auto px-1 pb-1">
            {groups.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No widget matches “{query}”.</p>}
            {groups.map(({ group, items }) => (
              <section key={group}>
                <h3 className="mb-2 text-xs font-medium text-muted-foreground">{group}</h3>
                <div className="grid gap-2 sm:grid-cols-2">
                  {items.map(([type, config]) => (
                    <Button
                      key={type}
                      variant="unstyled"
                      size="unstyled"
                      onClick={() => pickWidget(type)}
                      className="flex h-auto flex-col items-start gap-0.5 rounded-lg border border-border bg-card px-3 py-2.5 text-left transition-colors hover:border-primary hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="flex w-full items-center justify-between gap-2">
                        <span className="text-sm font-medium text-foreground">{config.title}</span>
                        {onTab.has(type) && <span className="text-xs text-muted-foreground">On this tab</span>}
                      </span>
                      <span className="text-xs font-normal text-muted-foreground">{config.description}</span>
                    </Button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* New dashboard tab */}
      <Dialog open={newTabOpen} onOpenChange={setNewTabOpen}>
        <DialogContent className="max-w-sm">
          <form onSubmit={createTab} className="space-y-4">
            <DialogHeader>
              <DialogTitle>New dashboard tab</DialogTitle>
              <DialogDescription>Group the widgets you want side by side, e.g. “HR” or “Support desk”.</DialogDescription>
            </DialogHeader>
            <Input autoFocus required value={newTabName} onChange={(e) => setNewTabName(e.target.value)} placeholder="Tab name" aria-label="Tab name" maxLength={40} />
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setNewTabOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={!newTabName.trim()}>Create tab</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
